import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, RotateCcw, ExternalLink, Save, Menu, X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Wordmark } from "../components/landing/Wordmark";
import { api, fetchAdminContent, saveContent, resetContent, logout as apiLogout } from "../lib/api";
import { NAV_GROUPS, CRM_TABS, CMS_TABS, CRM_TAB_IDS, PLANNER_TABS, PLANNER_TAB_IDS, RISKPROFILER_TABS, RISKPROFILER_TAB_IDS, SIFCONTROL_TABS, SIFCONTROL_TAB_IDS } from "../components/admin/AdminPrimitives";
import {
    BrandNavTab, SeoTab, HeroTab, TimesChangingTab, BenefitsTab, OurEdgeTab,
    PhilosophyTab, AboutTab, TestimonialsTab, FaqTab, CtaTab, FooterTab,
} from "../components/admin/SectionTabs";
import { IntegrationsTab } from "../components/admin/IntegrationsTab";
import { LeadsCRM } from "../components/crm/LeadsCRM";
import { ClientsCRM } from "../components/crm/ClientsCRM";
import { OnboardingCRM } from "../components/crm/OnboardingCRM";
import { BirthdaysCRM } from "../components/crm/BirthdaysCRM";
import { RemindersCRM } from "../components/crm/RemindersCRM";
import { PlannerCategoriesAdmin } from "../components/planner/PlannerCategoriesAdmin";
import { PlannerQuestionsAdmin } from "../components/planner/PlannerQuestionsAdmin";
import { PlannerPlansAdmin } from "../components/planner/PlannerPlansAdmin";
import { PlannerAnalyticsAdmin } from "../components/planner/PlannerAnalyticsAdmin";
import SifControlOverviewTab from "../components/admin/sifcontrol/OverviewTab";
import SifControlAmcsTab from "../components/admin/sifcontrol/AmcsTab";
import SifControlSifsTab from "../components/admin/sifcontrol/SifsTab";
import SifControlAmcUsersTab from "../components/admin/sifcontrol/AmcUsersTab";
import SifControlJobsTab from "../components/admin/sifcontrol/JobsTab";
import SifControlRankingsTab from "../components/admin/sifcontrol/RankingsTab";
import SifControlSettingsTab from "../components/admin/sifcontrol/SettingsTab";
import SifControlAmcUploadsTab from "../components/admin/sifcontrol/AmcUploadsTab";
import SifControlNavHistoryTab from "../components/admin/sifcontrol/NavHistoryTab";
import { RiskResponsesTab } from "../components/admin/riskprofiler/RiskResponsesTab";
import { RiskQuestionsTab } from "../components/admin/riskprofiler/RiskQuestionsTab";
import { RiskProfileSettingsTab } from "../components/admin/riskprofiler/RiskProfileSettingsTab";
import { RiskImportTab } from "../components/admin/riskprofiler/RiskImportTab";

const TAB_COMPONENT = {
    brand: BrandNavTab, seo: SeoTab, hero: HeroTab, times_changing: TimesChangingTab,
    benefits: BenefitsTab, our_edge: OurEdgeTab, philosophy: PhilosophyTab, about: AboutTab,
    testimonials: TestimonialsTab, faq: FaqTab, cta: CtaTab, footer: FooterTab, integrations: IntegrationsTab,
};

const CRM_COMPONENT = {
    crm_leads: LeadsCRM, crm_clients: ClientsCRM, crm_onboarding: OnboardingCRM, crm_birthdays: BirthdaysCRM, crm_reminders: RemindersCRM,
    planner_assets: PlannerCategoriesAdmin, planner_questions: PlannerQuestionsAdmin, planner_plans: PlannerPlansAdmin,
    planner_analytics: PlannerAnalyticsAdmin,
    rp_responses: RiskResponsesTab, rp_questions: RiskQuestionsTab, rp_settings: RiskProfileSettingsTab, rp_import: RiskImportTab,
    sc_overview: SifControlOverviewTab, sc_amcs: SifControlAmcsTab, sc_sifs: SifControlSifsTab,
    sc_amc_users: SifControlAmcUsersTab, sc_jobs: SifControlJobsTab,
    sc_amc_uploads: SifControlAmcUploadsTab,
    sc_nav_history: SifControlNavHistoryTab,
    sc_rankings: SifControlRankingsTab, sc_settings: SifControlSettingsTab,
};

const ALL_TABS = [...CRM_TABS, ...PLANNER_TABS, ...RISKPROFILER_TABS, ...SIFCONTROL_TABS, ...CMS_TABS];

const NavButton = ({ s, active, collapsed, onSelect }) => {
    const Icon = s.icon;
    return (
        <button
            onClick={onSelect}
            title={collapsed ? s.label : undefined}
            className={`w-full flex items-center gap-3 rounded-xl text-sm font-body transition-all duration-300 ${collapsed ? "lg:justify-center lg:px-0 px-4 py-2.5" : "px-4 py-2.5"} ${active ? "bg-[#0A0A0A] text-white" : "text-oth-ink2 hover:bg-white"}`}
            data-testid={`admin-tab-${s.id}`}
        >
            {Icon && <Icon size={16} className="shrink-0" />}
            <span className={collapsed ? "lg:hidden" : ""}>{s.label}</span>
        </button>
    );
};

const TabContent = ({ tab, content, update }) => {
    const Crm = CRM_COMPONENT[tab];
    if (Crm) return <Crm />;
    const Component = TAB_COMPONENT[tab];
    if (!Component) return null;
    return <Component content={content} update={update} />;
};

export default function AdminDashboard() {
    const [content, setContent] = useState(null);
    const [tab, setTab] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("tab") === "sifcontrol") return "sc_overview";
        if (params.get("gcal")) return "integrations";
        return "crm_leads";
    });
    const [saving, setSaving] = useState(false);
    const [, setMe] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => localStorage.getItem("oth_admin_rail") === "1");
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/auth/me").then((r) => setMe(r.data)).catch(() => navigate("/adminoth"));
        fetchAdminContent().then(setContent);
    }, [navigate]);

    const toggleCollapse = () => setCollapsed((c) => { localStorage.setItem("oth_admin_rail", c ? "0" : "1"); return !c; });

    const update = (path, value) => {
        setContent((c) => {
            const next = JSON.parse(JSON.stringify(c));
            const keys = path.split(".");
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]] = obj[keys[i]] || {};
            obj[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        try { await saveContent(content); toast.success("Content saved."); }
        catch (e) { console.error("Save content failed", e); toast.error("Could not save."); }
        finally { setSaving(false); }
    };

    const doReset = async () => {
        if (!window.confirm("Reset all content to defaults? This cannot be undone.")) return;
        try { const res = await resetContent(); setContent(res.data); toast.success("Content reset to defaults."); }
        catch (e) { console.error("Reset content failed", e); toast.error("Could not reset."); }
    };

    const logout = async () => {
        try { await apiLogout(); } catch (e) { console.debug("Logout API failed", e); }
        navigate("/adminoth");
    };

    const isCrm = CRM_TAB_IDS.includes(tab) || PLANNER_TAB_IDS.includes(tab) || RISKPROFILER_TAB_IDS.includes(tab) || SIFCONTROL_TAB_IDS.includes(tab);
    const activeLabel = useMemo(() => ALL_TABS.find((s) => s.id === tab)?.label, [tab]);

    if (!content) return <div className="min-h-screen flex items-center justify-center font-editorial italic text-oth-ink3" data-testid="admin-loading">Loading…</div>;

    const railW = collapsed ? "lg:w-[76px]" : "lg:w-[260px]";

    return (
        <div className="min-h-screen lg:flex" data-testid="admin-dashboard">
            <div className="noise-layer" aria-hidden />

            {/* Mobile top bar */}
            <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-white/95 backdrop-blur border-b border-oth-line/70 px-4 py-3" data-testid="admin-mobile-topbar">
                <button onClick={() => setSidebarOpen(true)} aria-label="Open menu" className="w-10 h-10 rounded-full bg-white border border-oth-line/70 flex items-center justify-center text-oth-ink" data-testid="admin-mobile-menu-btn"><Menu size={18} /></button>
                <div className="font-display text-base font-semibold tracking-tight truncate px-3">{activeLabel}</div>
                {isCrm ? <div className="w-10" /> : (
                    <button onClick={save} disabled={saving} aria-label="Save" className="w-10 h-10 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center disabled:opacity-50" data-testid="admin-mobile-save"><Save size={16} /></button>
                )}
            </div>

            <AnimatePresence>
                {sidebarOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSidebarOpen(false)} className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" data-testid="admin-drawer-backdrop" />
                )}
            </AnimatePresence>

            {/* Sidebar */}
            <aside
                className={`fixed lg:static top-0 left-0 z-50 h-full lg:h-auto w-[280px] ${railW} shrink-0 bg-white lg:bg-white/60 border-r border-oth-line/70 p-5 lg:p-4 flex flex-col transition-all duration-300 ease-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
                data-testid="admin-sidebar"
            >
                <div className="flex items-center justify-between">
                    <div className={collapsed ? "lg:hidden" : ""}><Wordmark /></div>
                    <button onClick={() => setSidebarOpen(false)} aria-label="Close menu" className="lg:hidden w-9 h-9 rounded-full border border-oth-line/70 flex items-center justify-center" data-testid="admin-drawer-close"><X size={16} /></button>
                    <button onClick={toggleCollapse} aria-label="Collapse sidebar" className="hidden lg:flex w-9 h-9 rounded-full border border-oth-line/70 items-center justify-center text-oth-ink2 hover:bg-white" data-testid="admin-collapse-btn">
                        {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
                    </button>
                </div>

                <nav className="mt-6 space-y-5 flex-1 overflow-y-auto thin-scroll">
                    {NAV_GROUPS.map((group) => (
                        <div key={group.title}>
                            <div className={`text-[10px] uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2 px-1 ${collapsed ? "lg:text-center lg:px-0" : ""}`}>
                                {collapsed ? <span className="lg:inline hidden">·</span> : group.title}
                                {collapsed && <span className="lg:hidden">{group.title}</span>}
                            </div>
                            <div className="space-y-1">
                                {group.tabs.map((s) => <NavButton key={s.id} s={s} active={tab === s.id} collapsed={collapsed} onSelect={() => { setTab(s.id); setSidebarOpen(false); }} />)}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="mt-6 space-y-1">
                    <a href="/" target="_blank" rel="noreferrer" className={`w-full flex items-center gap-3 text-sm font-body text-oth-ink2 hover:text-oth-ink ${collapsed ? "lg:justify-center px-4" : "px-4"} py-2.5 rounded-xl hover:bg-white transition-colors`} data-testid="admin-view-site">
                        <ExternalLink size={15} className="shrink-0" /><span className={collapsed ? "lg:hidden" : ""}>View site</span>
                    </a>
                    <button onClick={logout} className={`w-full flex items-center gap-3 text-sm font-body text-oth-ink2 hover:text-red-600 ${collapsed ? "lg:justify-center px-4" : "px-4"} py-2.5 rounded-xl hover:bg-white transition-colors`} data-testid="admin-logout">
                        <LogOut size={15} className="shrink-0" /><span className={collapsed ? "lg:hidden" : ""}>Sign out</span>
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 overflow-y-auto thin-scroll">
                <div className="hidden lg:flex sticky top-0 z-10 backdrop-blur-md bg-[rgba(245,245,242,0.85)] border-b border-oth-line/70 px-8 py-4 items-center justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body">{SIFCONTROL_TAB_IDS.includes(tab) ? "SIF Control" : RISKPROFILER_TAB_IDS.includes(tab) ? "Risk Profiler" : PLANNER_TAB_IDS.includes(tab) ? "DIY Planner" : isCrm ? "CRM" : "Editing section"}</div>
                        <div className="font-display text-xl font-semibold tracking-tight">{activeLabel}</div>
                    </div>
                    {!isCrm && (
                        <div className="flex items-center gap-3">
                            <button onClick={doReset} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-oth-line/70 text-sm font-body text-oth-ink2 hover:bg-white transition-colors" data-testid="admin-reset"><RotateCcw size={14} /> Reset defaults</button>
                            <button onClick={save} disabled={saving} className="btn-pill btn-pill-dark text-sm" data-testid="admin-save"><Save size={14} /> {saving ? "Saving..." : "Save changes"}</button>
                        </div>
                    )}
                </div>

                <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className={`p-4 md:p-6 lg:p-8 ${isCrm ? "max-w-[1280px]" : "max-w-[860px]"}`}>
                    <TabContent tab={tab} content={content} update={update} />
                </motion.div>
            </main>
        </div>
    );
}
